import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/apiClient';

const GUEST_CATEGORIES_KEY = 'guestCategories';

// Generate unique ID for guest categories
const generateId = () => `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// LocalStorage helpers
const getGuestCategories = () => {
    try {
        const stored = localStorage.getItem(GUEST_CATEGORIES_KEY);
        return stored ? JSON.parse(stored) : [];
    } catch {
        return [];
    }
};

const setGuestCategories = (categories) => {
    try {
        localStorage.setItem(GUEST_CATEGORIES_KEY, JSON.stringify(categories));
        // Dispatch custom event for cross-component synchronization
        window.dispatchEvent(new Event('guestCategoriesUpdated'));
    } catch (e) {
        // console.error('Failed to save guest categories:', e);
    }
};

/**
 * Custom hook for managing categories with hybrid storage
 * - Authenticated users: uses API + React Query
 * - Guest users: uses localStorage
 * 
 * @returns {Object} Category management functions and state
 */
export function useCategories() {
    const queryClient = useQueryClient();
    const [isAuthenticated, setIsAuthenticated] = useState(null);
    const [guestCategories, setLocalGuestCategories] = useState([]);
    const [isCheckingAuth, setIsCheckingAuth] = useState(true);

    // Check authentication status once on mount
    useEffect(() => {
        const checkAuth = async () => {
            try {
                const user = await apiClient.auth.me();
                setIsAuthenticated(!!user);
            } catch {
                setIsAuthenticated(false);
            }
            setIsCheckingAuth(false);
        };

        // Load guest categories immediately (will be used if not authenticated)
        setLocalGuestCategories(getGuestCategories());

        // Listen for updates from other components
        const handleStorageUpdate = () => {
            setLocalGuestCategories(getGuestCategories());
        };

        window.addEventListener('guestCategoriesUpdated', handleStorageUpdate);
        // Also listen to storage events for cross-tab sync (optional but good practice)
        window.addEventListener('storage', handleStorageUpdate);

        checkAuth();

        return () => {
            window.removeEventListener('guestCategoriesUpdated', handleStorageUpdate);
            window.removeEventListener('storage', handleStorageUpdate);
        };
    }, []);

    // API query for authenticated users
    const { data: user } = useQuery({
        queryKey: ['me'],
        queryFn: () => apiClient.auth.me(),
        enabled: isAuthenticated === true,
        staleTime: 1000 * 60 * 5,
    });

    const { data: apiCategories = [], isLoading: isLoadingApi } = useQuery({
        queryKey: ['userCategories'],
        queryFn: async () => {
            if (!user) return [];
            return apiClient.entities.UserCategory.list('-created_date');
        },
        enabled: isAuthenticated === true && !!user,
    });

    // API mutations for authenticated users
    const createMutation = useMutation({
        mutationFn: (data) => apiClient.entities.UserCategory.create(data),
        onSuccess: () => {
            queryClient.refetchQueries({ queryKey: ['userCategories'], type: 'all' });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (id) => apiClient.entities.UserCategory.delete(id),
        onSuccess: () => {
            queryClient.refetchQueries({ queryKey: ['userCategories'], type: 'all' });
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, data }) => apiClient.entities.UserCategory.update(id, data),
        onSuccess: () => {
            queryClient.refetchQueries({ queryKey: ['userCategories'], type: 'all' });
        },
    });

    // Unified categories list
    const categories = useMemo(() => {
        if (isAuthenticated === true) {
            return apiCategories;
        }
        // Use guest categories while checking auth or if not authenticated
        return guestCategories;
    }, [isAuthenticated, apiCategories, guestCategories]);

    // Get categories for a specific product
    const getProductCategories = useCallback((productId) => {
        return categories.filter(c => String(c.product_id) === String(productId));
    }, [categories]);

    // Get all unique category names (for autocomplete)
    const allCategoryNames = useMemo(() => {
        return [...new Set(categories.map(c => c.category_name))];
    }, [categories]);

    // Add category
    const addCategory = useCallback(async (productId, categoryName) => {
        // Check for duplicates - use String() for type-safe comparison
        const exists = categories.some(
            c => String(c.product_id) === String(productId) && c.category_name === categoryName
        );
        if (exists) {
            // console.warn('Category already exists for this product');
            return;
        }

        if (isAuthenticated === true) {
            // Use API
            await createMutation.mutateAsync({
                product_id: productId,
                category_name: categoryName
            });
        } else {
            // Use localStorage
            const newCategory = {
                id: generateId(),
                product_id: productId,
                category_name: categoryName,
                created_at: new Date().toISOString()
            };
            const updated = [newCategory, ...guestCategories];



            setGuestCategories(updated);
            setLocalGuestCategories(updated);
        }
    }, [isAuthenticated, categories, guestCategories, createMutation]);

    // Remove category
    const removeCategory = useCallback(async (categoryId) => {
        if (isAuthenticated === true) {
            // Use API
            await deleteMutation.mutateAsync(categoryId);
        } else {
            // Use localStorage
            const updated = guestCategories.filter(c => c.id !== categoryId);
            setGuestCategories(updated);
            setLocalGuestCategories(updated);
        }
    }, [isAuthenticated, guestCategories, deleteMutation]);

    // Update category (rename)
    const updateCategory = useCallback(async (categoryId, newName) => {
        if (isAuthenticated === true) {
            // Use API
            await updateMutation.mutateAsync({ id: categoryId, data: { category_name: newName } });
        } else {
            // Use localStorage
            const updated = guestCategories.map(c =>
                c.id === categoryId ? { ...c, category_name: newName } : c
            );
            setGuestCategories(updated);
            setLocalGuestCategories(updated);
        }
    }, [isAuthenticated, guestCategories, updateMutation]);

    return {
        categories,
        getProductCategories,
        allCategoryNames,
        addCategory,
        removeCategory,
        updateCategory,
        isLoading: isCheckingAuth || (isAuthenticated && isLoadingApi),
        isAuthenticated,
        user,
    };
}

export default useCategories;
